import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createRelayStatusType,
  deleteRelayStatusType,
  getRelayStatusTypeById,
  getRelayStatusTypesList,
  updateRelayStatusType,
  updateRelayStatusTypeStatus,
  type RelayStatusTypeListParams,
  type RelayStatusTypePayload,
  type RelayStatusTypeStatusPayload,
} from "../../services/administrator/relayStatusTypesService";

export const useQueryGetRelayStatusTypesList = (
  params: RelayStatusTypeListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.relayStatusTypeList, params, language],
      queryFn: () => getRelayStatusTypesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetRelayStatusTypeById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.relayStatusTypeDetail, id, language],
    queryFn: () => getRelayStatusTypeById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateRelayStatusType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: RelayStatusTypePayload) =>
      createRelayStatusType(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.relayStatusTypeList,
      }),
  });
};

export const useMutationUpdateRelayStatusType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: RelayStatusTypePayload & { id: string | number };
    }) => updateRelayStatusType(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.relayStatusTypeList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.relayStatusTypeDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateRelayStatusTypeStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, active }: RelayStatusTypeStatusPayload) =>
      updateRelayStatusTypeStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.relayStatusTypeList,
      }),
  });
};

export const useMutationDeleteRelayStatusType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteRelayStatusType(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.relayStatusTypeList,
      }),
  });
};
